// @flow
/* -----------------Globals--------------- */
import React, {
  useState,
} from 'react';

/* -----------------Third parties--------------- */
import dynamic from 'next/dynamic';
import clsx from 'clsx';

/* -----------------UI--------------- */
import {
  Grid,
  Box,
} from '@material-ui/core';
import {
  withStyles,
} from '@material-ui/styles/';
import {
  FlagOutline,
} from 'mdi-material-ui';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Skeleton from '@material-ui/lab/Skeleton';

/* -----------------Types--------------- */
import type {
  Term,
  TermMetadata,
} from 'common/types';
import {
  termMenuOption,
  type TermMenuOptions,
} from './types';

/* -----------------Utils--------------- */
import {
  getMetadata,
} from 'common/utils';
import {
  isEmptyList,
} from 'common/simpleUtils';
import {
  defaultMeta,
} from 'common/content/constants';
import {
  normalizeName,
} from 'common/content/normalize';
import {
  authenticated,
  key,
} from '../../common/authutils';

/* -----------------Child components--------------- */
import Facts from './Facts';
import TermDetails from './TermDetails';
import Menus from './Menus';
import ProgressRingUnAuth from './ProgressRingUnAuth';
import ListTags from '@components/common/ListTags';

/* -----------------Lazy components--------------- */
const EditTerm = dynamic(() => import('../create/DialogEditTerm'), { ssr: false });
const ProgressRing = dynamic(() => import('./ProgressRing'), {
  ssr: false,
  loading: () => (
    <Skeleton
      variant="circle"
      width={40}
      height={40}
    />
  ),
});
const SuggestCorrection = dynamic(() => import('./dialogs/SuggestCorrection'), { ssr: false });

/* -----------------Styles--------------- */
const styles = () => ({
  containerGridBlur: {
    filter: 'blur(0.3rem)',
  },
});
/* -----------------Props--------------- */
type Classes = {
  containerGridBlur: string
}

type Props = {
  term: Term,
  metadata: TermMetadata,
 // showEditMenuOption: boolean,
  studySetId: string,
  loading?: boolean,
  showBlur?: boolean,
  classes: Classes,
 };

/* -----------------Component--------------- */
const TermCard = (props: Props) => {
  const {
    studySetId,
    loading,
    classes,
    showBlur,
    term: {
      id,
      images,
      facts,
      name,
      // $FlowFixMe
      tags = [],
    },
    term,
    metadata = defaultMeta,
  //  showEditMenuOption,
  } = props;
  const finalName = normalizeName(name);
  const isAuthenticated = authenticated(key.loggedIn);

  const showSuggestCorrection = isAuthenticated;
  // $FlowFixMe
  const finalMeta = getMetadata(facts.length, metadata);
  const termMenuData = [
    // ...(showEditMenuOption ? [
    //   {
    //     label: 'Edit',
    //     icon: <PencilOutline />,
    //   },
    // ] : []),
    ...(showSuggestCorrection ? [{
      label: 'Suggest correction',
      icon: <FlagOutline />,
    }] : []),
  ];
  const [suggestDialogVisible, setSuggestDialogVisible] = useState(false);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [showEdit, setShowEdit] = useState(false);

  const handleMenuItemClick = (item: TermMenuOptions) => {
    switch (item) {
      case termMenuOption.Edit:
        setShowEdit(true);
        break;
      case termMenuOption.SuggestionCorrection:
        setSuggestDialogVisible(true);
        break;
      default:
        break;
    }
  };
  const handleEndSuggestion = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setSuggestDialogVisible(false);
  };

  const MenuOptions = () => (
    <Menus
      sortOptions={termMenuData}
      moreMenu
      handleMenuItemClick={item => handleMenuItemClick(item.label)}
    >
      {item => (
        <Box display="flex" flexDirection="row" alignItems="center">
          <Box mr={3} display="flex" alignItems="center">
            {item.icon}
          </Box>
          {item.label}
        </Box>
      )}
    </Menus>
  );
  return (
    <>
      {!isMobile
        ? (
          <Box pr={2}>
            {isAuthenticated ? (
              <ProgressRing
                distribution={finalMeta.distribution}
                radius={20}
                innerRadius={14}
                loading={loading}
              />
            ) : (
              <ProgressRingUnAuth
                radius={20}
                borderWidth={5}
              />
            )}

          </Box>
        ) : null }
      <Grid
        container
        spacing={1}
        className={clsx(showBlur && classes.containerGridBlur)}
      >
        <Grid
          item
          container
          display="flex"
          alignItems={images && images.length ? 'flex-start' : 'center'}
          xs={12}
          sm={12}
          md={6}
        >
          <Box
            width="100%"
            display="flex"
            flexWrap="nowrap"
            overflow="hidden"
          >
            <TermDetails termId={id} termImages={images} termName={finalName} />

            {isMobile
              ? (
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  height="48px"
                  alignItems="center"
                  mt={-1}
                  pl={2}
                  mr={!isEmptyList(termMenuData) ? -1 : 0}
                >
                  {isAuthenticated ? (
                    <ProgressRing
                      distribution={finalMeta.distribution}
                      radius={16}
                      innerRadius={11}
                      loading={loading}
                    />
                  ) : (
                    <ProgressRingUnAuth
                      radius={16}
                      borderWidth={4}
                    />
                  )}
                  {!isEmptyList(termMenuData) ? <MenuOptions /> : null}
                </Box>
              ) : null
          }
          </Box>
          { tags ? (
            <Box
              pt={2}
              pl={isMobile ? 1 : 0}
            >
              <ListTags tags={tags} />
            </Box>
          ) : null }
        </Grid>
        <Grid
          item
          container
          display="flex"
          alignItems="center"
          xs={12}
          sm={12}
          md={6}
        >
          <Box
            width="100%"
            pl={isMobile ? 1 : 0}
          >
            <Facts facts={facts} />
          </Box>
        </Grid>
      </Grid>
      {!isMobile && !isEmptyList(termMenuData)
        ? (
          <MenuOptions />
        ) : null}
      {suggestDialogVisible ? (
        <SuggestCorrection
          onDismiss={handleEndSuggestion}
          visible={suggestDialogVisible}
          itemId={id}
        />
      ) : null}
      {showEdit ? (
        <EditTerm
          visible={showEdit}
          handleClose={() => setShowEdit(false)}
          term={term}
          studySetId={studySetId}
        />
      ) : null}
    </>
  );
};

export default withStyles(styles)(TermCard);
